import { useState } from "react";
import { toast } from "sonner";
import { Clock, Mail, MapPin, Phone, Send, Truck } from "lucide-react";
import { FadeUp, Eyebrow } from "../components/Reveal";
import { HalalBadge } from "../components/Navbar";
import { api, formatApiError } from "../lib/api";
import { DELIVERY_NOTICE } from "../data/site";
import { useSEO } from "../lib/useSEO";

const CARDS = [
  { icon: Phone, title: "Call the kitchen", lines: ["(571) 833-1906", "Mon–Sat, 9am–7pm"], testid: "contact-card-phone" },
  { icon: Mail, title: "Write to us", lines: ["eathiplate@gmail.com"], testid: "contact-card-email" },
  { icon: MapPin, title: "Our kitchen", lines: ["Woodbridge, VA", "Serving the entire DMV"], testid: "contact-card-location" },
  { icon: Clock, title: "Delivery windows", lines: ["Sun–Thu: 4pm–9pm", "Friday catering by booking"], testid: "contact-card-hours" },
];

const DMV_AREAS = [
  { region: "Washington DC", places: "All wards — Georgetown to Capitol Hill" },
  { region: "Maryland", places: "Bethesda · Rockville · Silver Spring · College Park · Hyattsville" },
  { region: "Northern Virginia", places: "Arlington · Alexandria · Fairfax · Falls Church · Woodbridge · Manassas" },
];

export default function Contact() {
  useSEO({ title: "Contact HiPlate — Halal Meal Prep & Catering, Woodbridge VA", description: "Call (571) 833-1906 or email eathiplate@gmail.com. Delivery-only halal meal prep and catering serving DC, Maryland and Northern Virginia.", path: "/contact" });
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General question", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full rounded-xl border border-inkline bg-parchment px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-muted2/60 focus:border-spice";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we usually reply within a few hours.");
      setForm({ name: "", email: "", phone: "", subject: "General question", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="bg-parchment">
      <section className="grain relative overflow-hidden bg-ink pb-16 pt-40 text-parchment lg:pt-48" data-testid="contact-hero">
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-8">
          <HalalBadge />
          <h1 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Talk to <span className="italic text-saffron">the kitchen.</span>
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-parchment/70">
            Questions about this week's menu, a catering quote, or a delivery window — we answer fast.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24" data-testid="contact-section">
        <div className="grid gap-10 lg:grid-cols-2">
          <FadeUp>
            <form onSubmit={submit} className="space-y-4 rounded-3xl border border-inkline bg-cream p-7 sm:p-9" data-testid="contact-form">
              <div>
                <Eyebrow>Send a message</Eyebrow>
                <h2 className="mt-2 font-display text-2xl font-semibold text-ink">We reply within hours, not days.</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input required data-testid="contact-name" placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
                <input required type="email" data-testid="contact-email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="contact-phone" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} />
                <select data-testid="contact-subject" value={form.subject} onChange={set("subject")} className={inputCls}>
                  <option>General question</option>
                  <option>Meal prep order</option>
                  <option>Catering</option>
                  <option>Delivery area</option>
                  <option>Feedback</option>
                </select>
              </div>
              <textarea required data-testid="contact-message" rows={5} placeholder="How can we help?" value={form.message} onChange={set("message")} className={inputCls} />
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={sending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-spice py-4 text-sm font-bold text-parchment transition-colors duration-300 hover:bg-ink disabled:opacity-60"
              >
                <Send size={15} /> {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </FadeUp>

          <div className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              {CARDS.map((c, i) => (
                <FadeUp key={c.title} delay={i * 0.07}>
                  <div className="h-full rounded-2xl border border-inkline bg-cream p-6" data-testid={c.testid}>
                    <c.icon size={22} className="text-spice" />
                    <h3 className="mt-3 font-display text-lg font-semibold text-ink">{c.title}</h3>
                    {c.lines.map((l) => (
                      <p key={l} className="mt-1 text-sm text-muted2">{l}</p>
                    ))}
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.2}>
              <div className="rounded-3xl bg-ink p-7 text-parchment sm:p-8" data-testid="service-area-card">
                <div className="flex items-center gap-2.5">
                  <Truck size={20} className="text-saffron" />
                  <h3 className="font-display text-xl font-semibold">DMV service area</h3>
                </div>
                <div className="mt-5 space-y-4">
                  {DMV_AREAS.map((a) => (
                    <div key={a.region} className="border-l-2 border-saffron/50 pl-4">
                      <div className="text-sm font-bold">{a.region}</div>
                      <div className="mt-0.5 text-xs text-parchment/60">{a.places}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-6 rounded-xl bg-parchment/10 p-3.5 text-xs leading-relaxed text-parchment/80" data-testid="contact-delivery-notice">
                  {DELIVERY_NOTICE}
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
